/**
 * decimal-gen — 康軒數學三下「第 5 單元 小數」考卷產生器。
 * 對照 `circle-gen.ts` / `mul-div-gen.ts` / `stats-table-gen.ts` 同一套 lockdown 架構
 *(**seeded RNG + 策展閉集 / 受控變數 + 程式可驗證答案 + validator + 200-seed regression**)。
 *
 * grounded 在康軒三下第 5 章課本真實內容(boss 拍攝 p63–78 + 練習百分百五),五個題型區塊:
 *   ① convert 分數↔一位小數互換(✅ 可驗證,唯一真相)—— `a/10 = 0.a`、`0.N = N/10`。
 *      含「N 個 0.1 = 0.N」「幾個 1 + 幾個 0.1」位值題(KP1/KP2/KP3)。
 *   ② figure 看圖 / 數線寫小數(✅ 可驗證)—— 塗 N/10 格 = 0.N(塗色題);數線指定點寫小數
 *      (數線題)。recompute by 十分位整數。
 *   ③ compare 一位小數比大小(✅ 可驗證)—— 比 `>`/`<`/`=`,用十分位整數比避免浮點誤差(KP4)。
 *   ④ addsub 一位小數加減(✅ 可驗證)—— 同十分位相加減,含進位 / 退位 / 帶小數,十分位整數運算(KP5)。
 *   ⑤ apply 帶小數與應用(✅ 可驗證)—— 毫米↔公分換算 + 元 / 公升 / 公斤 / 公尺情境加減(KP6)。
 *
 * **浮點防呆鐵則(核心)**:本檔**所有**小數運算一律用「十分位整數」算 ——
 *   把 x 轉成 `t = round(x*10)`(0.1 → 1、2.3 → 23),整數加減 / 比大小,結果再 `/10` 還原。
 *   杜絕 `0.1+0.2 = 0.30000000000000004` 的 IEEE-754 誤差。`fmtDecimal` / `recomputeDecimalAnswer`
 *   共用此核心,validator 重算後比對。
 *
 * 鐵則 / 設計(對齊 feedback_generator_source_lockdown,不打地鼠):
 *   - **seeded RNG**(別用 Math.random):同 seed → 同卷,可重現;UI「重抽」= 換 seed。
 *   - **答案程式可驗證**:每題 answer 由 `recomputeDecimalAnswer` 反算,validator 重算後比對。
 *   - **不超綱**:只到**一位小數**(小數點後一位);**不出兩位小數 / 百分位 / 小數乘除**(四上)。
 *     整數部 0..20、十分位 0..9;加減結果非負且 ≤ 約 20(課本範圍)。
 *   - **lockdown 防壞題**:模板 = 策展閉集常數;硬規則寫進產生器 + validator;200-seed regression 全過。
 *
 * 學習/遊戲分離:純學習側,**絕不**碰 commanderXp / 戰鬥數值。
 */

// ───────────────────────────── 十分位整數核心(浮點防呆)─────────────────────────────

/** 一位小數 → 十分位整數(0.1→1、2.3→23)。四捨五入消浮點尾差。 */
export function toTenths(x: number): number {
  return Math.round(x * 10);
}

/** 十分位整數 → 一位小數值(13→1.3)。回 number(僅供顯示前格式化用)。 */
export function fromTenths(t: number): number {
  return t / 10;
}

/**
 * 把「十分位整數」格式化成課本式一位小數字串:
 *   - 整除 10(整數)→ 帶 `.0`(課本進位寫 `1.0`、`2.0`,強調是小數運算結果)。
 *   - 其餘 → 一位小數(`13` → `1.3`、`7` → `0.7`)。
 * 一律從整數還原,**不經瀏覽器浮點加減**(防呆)。
 */
export function fmtTenths(t: number): string {
  const intPart = Math.trunc(t / 10);
  const frac = Math.abs(t % 10);
  return `${intPart}.${frac}`;
}

/**
 * 把一位小數值格式化成字串(供 prompt 顯示),先轉十分位整數再還原(防呆)。
 * 例:fmtDecimal(0.1+0.2 的數值意圖) → 一律先 toTenths 再 fmtTenths。
 */
export function fmtDecimal(x: number): string {
  return fmtTenths(toTenths(x));
}

// ───────────────────────────── 型別 ─────────────────────────────

/** 題型(五種)。 */
export type DecimalQuestionKind = 'convert' | 'figure' | 'compare' | 'addsub' | 'apply';

/**
 * 一題的反算規格(closed set)。validator / 線上批改依 `op` 重算 → 與 `answer` 比對。
 * 所有帶小數的欄位都以**十分位整數**儲存(`tenths` / `aT` / `bT`…),recompute 時還原,杜絕浮點。
 *   - 'frac-to-dec':分數 `n/10` → 一位小數 `0.n`(n=1..9)。
 *   - 'dec-to-frac':一位小數 `0.n` → 分數 `n/10`(答 `n/10` 字串)。
 *   - 'count-tenths':`k 個 0.1` → 一位小數(k 可 >10,進位;答 = fmtTenths(k))。
 *   - 'place-value':`ones 個 1 + tenths 個 0.1` → 帶小數(答 = fmtTenths(ones*10+tenths))。
 *   - 'shade':塗 n/10 格 → 0.n(塗色題,圖示 n 格;答 0.n)。
 *   - 'numberline':數線 0..1(或 0..maxInt)取一點 = tenths/10(答一位小數)。
 *   - 'compare':比 aT 與 bT(十分位整數)→ 答 '>' / '<' / '='。
 *   - 'add' / 'sub':aT ± bT(十分位整數)→ 答 fmtTenths(結果)。
 *   - 'mm-to-cm':n 毫米 → 0.n 公分(n=1..9)。
 *   - 'cm-mm-to-dec':a 公分 b 毫米 → (a*10+b)/10 公分。
 *   - 'apply-add' / 'apply-sub':情境加減(aT ± bT),帶單位(unit)。
 */
export type DecimalVerify =
  | { op: 'frac-to-dec'; numerator: number }
  | { op: 'dec-to-frac'; tenths: number }
  | { op: 'count-tenths'; count: number }
  | { op: 'place-value'; ones: number; tenths: number }
  | { op: 'shade'; shaded: number }
  | { op: 'numberline'; tenths: number; maxInt: number }
  | { op: 'compare'; aT: number; bT: number }
  | { op: 'add'; aT: number; bT: number }
  | { op: 'sub'; aT: number; bT: number }
  | { op: 'mm-to-cm'; mm: number }
  | { op: 'cm-mm-to-dec'; cm: number; mm: number }
  | { op: 'apply-add'; aT: number; bT: number; unit: string }
  | { op: 'apply-sub'; aT: number; bT: number; unit: string };

/** 一道小數題:題幹 + 程式可驗證答案 + 反算用 verify。 */
export interface DecimalQuestion {
  id: string;
  /** 題幹(顯示 / 列印用)。 */
  prompt: string;
  /** 正解人類可讀字串(顯示 / 批改用)。 */
  answer: string;
  /**
   * 比大小題的選項(compare 用,固定 ['>','<','=']);其餘題型 undefined。
   * 線上版可用選項按鈕,A4 版圈選。
   */
  choices?: readonly string[];
  /**
   * 圖示資料(figure 大題用):塗色題給「總格 10、塗 N 格」;數線題給「分段數、指定點」。
   * 線上 / A4 都用它畫格子 / 數線。其餘題型 undefined。
   */
  figure?:
    | { kind: 'shade'; total: number; shaded: number }
    | { kind: 'numberline'; maxInt: number; tenths: number };
  /** 反算規格(validator / 批改重算用)。 */
  verify: DecimalVerify;
}

/** 一大題 = 一種題型 + 題目陣列 + 標籤 / 標題 / 配分。 */
export interface DecimalSection {
  kind: DecimalQuestionKind;
  /** 大題標籤,例 '一'。 */
  label: string;
  /** 大題標題,例 '分數與一位小數的互換'。 */
  title: string;
  /** 作答指示語。 */
  instructions: string;
  /** 每題配分。 */
  pointsEach: number;
  /** 該大題的題目。 */
  questions: DecimalQuestion[];
}

/** 一份小數考卷。 */
export interface DecimalExam {
  id: string;
  title: string;
  /** 教科書版本(數學 = 康軒)。 */
  textbook: '康軒';
  /** 範圍標註(顯示用)。 */
  scope: string;
  sections: DecimalSection[];
}

/** 各題型題數(預設見 DEFAULT_COUNTS;key = DecimalQuestionKind)。 */
export type DecimalCounts = Partial<Record<DecimalQuestionKind, number>>;

/** `generateDecimalExam` 選項。 */
export interface DecimalGenOpts {
  /** 可重現種子(同 seed 同卷);UI「重抽」= 換 seed。 */
  seed: number;
  /** 各題型題數,預設見 DEFAULT_COUNTS。 */
  counts?: DecimalCounts;
  /** 卷 id(預設 `decimal-<seed>`)。 */
  id?: string;
  /** 卷名(預設 `小數考卷 · 第 <seed> 回`)。 */
  title?: string;
}

// ───────────────────────────── seeded RNG ─────────────────────────────

/**
 * 確定性 RNG —— mulberry32(與 circle-gen / mul-div-gen / stats-table-gen 同款,
 * 純函式可重現)。注入 seed,杜絕 Math.random。
 */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 抽一個元素。 */
function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

/** [lo, hi] 含端點整數。 */
function randInt(rng: () => number, lo: number, hi: number): number {
  return lo + Math.floor(rng() * (hi - lo + 1));
}

// ───────────────────────────── 受控閉集 ─────────────────────────────

/** 分數↔小數互換用的分子(1..9,分母固定 10 → 純一位小數 0.1..0.9)。 */
const FRAC_NUMERATORS = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

/** 帶小數整數部候選(1..9,使帶小數 ≤ 9.9,不超綱)。 */
const ONES_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8] as const;

/** 比大小選項(固定三選一)。 */
const COMPARE_CHOICES = ['>', '<', '='] as const;

/** ⑤ 應用題情境(grounded 課本:果汁 / 礦泉水 公升、麵粉 公斤、身高 公尺、緞帶 公分)。 */
interface ApplyTemplate {
  /** 生題函式:回 {prompt, op, aT, bT, unit}。op='apply-add'|'apply-sub'。 */
  unit: string;
  /** 'add' | 'sub'。 */
  mode: 'add' | 'sub';
  /** 題幹模板(用 {a} {b} 代入一位小數字串)。 */
  template: string;
}

const APPLY_TEMPLATES: readonly ApplyTemplate[] = [
  { unit: '公升', mode: 'add', template: '一瓶果汁有 {a} 公升,又倒入 {b} 公升,現在共有幾公升?' },
  { unit: '公升', mode: 'sub', template: '一桶礦泉水原有 {a} 公升,喝掉 {b} 公升,還剩下幾公升?' },
  { unit: '公斤', mode: 'add', template: '一包麵粉 {a} 公斤,再加一包 {b} 公斤,合起來是幾公斤?' },
  { unit: '公斤', mode: 'sub', template: '一袋糖原有 {a} 公斤,用掉 {b} 公斤,還剩下幾公斤?' },
  { unit: '公尺', mode: 'add', template: '長頸鹿原本身高 {a} 公尺,長高了 {b} 公尺,現在身高幾公尺?' },
  { unit: '公分', mode: 'add', template: '一條緞帶長 {a} 公分,接上另一條 {b} 公分,合起來幾公分?' },
  { unit: '公分', mode: 'sub', template: '一條繩子長 {a} 公分,剪掉 {b} 公分,還剩幾公分?' },
] as const;

// ───────────────────────────── 反算核心(唯一真相)─────────────────────────────

/**
 * 依一題的 `verify` 規格,重算期望答案字串。validator 與線上批改共用此函式(單一真相)。
 * 規格不合法(越界 / 超綱 / 負結果…)回 null(視為壞題)。
 *
 * **全程十分位整數運算**(浮點防呆):任何帶小數的計算都在整數域完成,最後 `fmtTenths` 還原。
 */
export function recomputeDecimalAnswer(v: DecimalVerify): string | null {
  switch (v.op) {
    case 'frac-to-dec': {
      if (!Number.isInteger(v.numerator)) return null;
      if (v.numerator < 1 || v.numerator > 9) return null; // 純一位小數 0.1..0.9
      return fmtTenths(v.numerator); // n/10 → 0.n
    }
    case 'dec-to-frac': {
      if (!Number.isInteger(v.tenths)) return null;
      if (v.tenths < 1 || v.tenths > 9) return null; // 0.1..0.9 → n/10
      return `${v.tenths}/10`;
    }
    case 'count-tenths': {
      if (!Number.isInteger(v.count)) return null;
      if (v.count < 1 || v.count > 99) return null; // 1..99 個 0.1(≤ 9.9)
      return fmtTenths(v.count);
    }
    case 'place-value': {
      if (!Number.isInteger(v.ones) || !Number.isInteger(v.tenths)) return null;
      if (v.ones < 0 || v.ones > 9) return null;
      if (v.tenths < 0 || v.tenths > 19) return null; // 容許 10..19 個 0.1(課本含進位例 9.2=8+12)
      const total = v.ones * 10 + v.tenths;
      if (total < 1 || total > 99) return null;
      return fmtTenths(total);
    }
    case 'shade': {
      if (!Number.isInteger(v.shaded)) return null;
      if (v.shaded < 1 || v.shaded > 9) return null; // 塗 1..9 格 / 10
      return fmtTenths(v.shaded);
    }
    case 'numberline': {
      if (!Number.isInteger(v.tenths) || !Number.isInteger(v.maxInt)) return null;
      if (v.maxInt < 1 || v.maxInt > 5) return null;
      if (v.tenths < 1 || v.tenths > v.maxInt * 10 - 1) return null; // 不落在整數端點
      return fmtTenths(v.tenths);
    }
    case 'compare': {
      if (!Number.isInteger(v.aT) || !Number.isInteger(v.bT)) return null;
      if (v.aT < 0 || v.aT > 200 || v.bT < 0 || v.bT > 200) return null;
      return v.aT > v.bT ? '>' : v.aT < v.bT ? '<' : '=';
    }
    case 'add': {
      if (!Number.isInteger(v.aT) || !Number.isInteger(v.bT)) return null;
      if (v.aT < 0 || v.bT < 0) return null;
      const sum = v.aT + v.bT;
      if (sum < 1 || sum > 200) return null; // 結果 ≤ 20.0
      return fmtTenths(sum);
    }
    case 'sub': {
      if (!Number.isInteger(v.aT) || !Number.isInteger(v.bT)) return null;
      const diff = v.aT - v.bT;
      if (diff < 0) return null; // 不出負數
      if (v.aT > 200) return null;
      return fmtTenths(diff);
    }
    case 'mm-to-cm': {
      if (!Number.isInteger(v.mm)) return null;
      if (v.mm < 1 || v.mm > 9) return null; // 1..9 毫米 → 0.1..0.9 公分
      return `${fmtTenths(v.mm)} 公分`;
    }
    case 'cm-mm-to-dec': {
      if (!Number.isInteger(v.cm) || !Number.isInteger(v.mm)) return null;
      if (v.cm < 0 || v.cm > 19) return null;
      if (v.mm < 0 || v.mm > 9) return null;
      const t = v.cm * 10 + v.mm;
      if (t < 1) return null;
      return `${fmtTenths(t)} 公分`;
    }
    case 'apply-add': {
      if (!Number.isInteger(v.aT) || !Number.isInteger(v.bT)) return null;
      if (v.aT < 0 || v.bT < 0) return null;
      const sum = v.aT + v.bT;
      if (sum < 1 || sum > 300) return null; // 應用題容許略大(≤30）
      if (!v.unit) return null;
      return `${fmtTenths(sum)} ${v.unit}`;
    }
    case 'apply-sub': {
      if (!Number.isInteger(v.aT) || !Number.isInteger(v.bT)) return null;
      const diff = v.aT - v.bT;
      if (diff < 0) return null;
      if (v.aT > 300) return null;
      if (!v.unit) return null;
      return `${fmtTenths(diff)} ${v.unit}`;
    }
  }
}

// ───────────────────────────── 各題型生成(從規格反算 answer)─────────────────────────────

/**
 * ① convert 分數↔一位小數互換 + 位值。輪流出四型:
 *   frac→dec(n/10 → 0.n)、dec→frac(0.n → n/10)、count-tenths(k 個 0.1)、place-value(幾個1+幾個0.1)。
 */
function genConvertQuestions(rng: () => number, n: number): DecimalQuestion[] {
  const out: DecimalQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const t = i % 4;
    if (t === 0) {
      const numerator = pick(rng, FRAC_NUMERATORS);
      out.push({
        id: `cv${i + 1}`,
        prompt: `把分數 ${numerator}/10 寫成一位小數是多少?`,
        answer: recomputeDecimalAnswer({ op: 'frac-to-dec', numerator })!,
        verify: { op: 'frac-to-dec', numerator },
      });
    } else if (t === 1) {
      const tenths = pick(rng, FRAC_NUMERATORS);
      out.push({
        id: `cv${i + 1}`,
        prompt: `把小數 ${fmtTenths(tenths)} 寫成分母是 10 的分數是多少?`,
        answer: recomputeDecimalAnswer({ op: 'dec-to-frac', tenths })!,
        verify: { op: 'dec-to-frac', tenths },
      });
    } else if (t === 2) {
      // count-tenths:含可進位(11..17 個)的帶小數,grounded 課本「17 個 0.1=1.7」。
      const count = rng() < 0.5 ? randInt(rng, 1, 9) : randInt(rng, 11, 19);
      out.push({
        id: `cv${i + 1}`,
        prompt: `${count} 個 0.1 合起來是多少?`,
        answer: recomputeDecimalAnswer({ op: 'count-tenths', count })!,
        verify: { op: 'count-tenths', count },
      });
    } else {
      // place-value:幾個 1 + 幾個 0.1(tenths 0..9,純位值)。
      const ones = pick(rng, ONES_OPTIONS);
      const tenths = randInt(rng, 1, 9);
      out.push({
        id: `cv${i + 1}`,
        prompt: `${ones} 個 1 和 ${tenths} 個 0.1 合起來是多少?`,
        answer: recomputeDecimalAnswer({ op: 'place-value', ones, tenths })!,
        verify: { op: 'place-value', ones, tenths },
      });
    }
  }
  return out;
}

/**
 * ② figure 看圖 / 數線寫小數。交替出塗色題(總 10 格塗 N)與數線題(0..maxInt 指定點)。
 */
function genFigureQuestions(rng: () => number, n: number): DecimalQuestion[] {
  const out: DecimalQuestion[] = [];
  for (let i = 0; i < n; i++) {
    if (i % 2 === 0) {
      // 塗色題:一張平分 10 格,塗 shaded 格 = 0.shaded 張。
      const shaded = randInt(rng, 1, 9);
      out.push({
        id: `fg${i + 1}`,
        prompt: `下面一張紙平分成 10 格,塗了 ${shaded} 格,塗色部分用小數表示是多少張?`,
        answer: recomputeDecimalAnswer({ op: 'shade', shaded })!,
        figure: { kind: 'shade', total: 10, shaded },
        verify: { op: 'shade', shaded },
      });
    } else {
      // 數線題:0..maxInt,每大格分 10 小格,箭頭指 tenths/10。
      const maxInt = randInt(rng, 1, 3);
      const tenths = (() => {
        // 不落在整數端點(留小數)。
        let cand = randInt(rng, 1, maxInt * 10 - 1);
        while (cand % 10 === 0) cand = randInt(rng, 1, maxInt * 10 - 1);
        return cand;
      })();
      out.push({
        id: `fg${i + 1}`,
        prompt: `下面的數線從 0 到 ${maxInt},每一大格平分成 10 小格,箭頭(↑)指的數是多少?`,
        answer: recomputeDecimalAnswer({ op: 'numberline', tenths, maxInt })!,
        figure: { kind: 'numberline', maxInt, tenths },
        verify: { op: 'numberline', tenths, maxInt },
      });
    }
  }
  return out;
}

/**
 * ③ compare 一位小數比大小。混出純小數(0.x vs 0.y)與帶小數(a.x vs b.y);**用十分位整數比**。
 * 保證至少出現一題 '>' 與一題 '<'(n≥2)。
 */
function genCompareQuestions(rng: () => number, n: number): DecimalQuestion[] {
  const out: DecimalQuestion[] = [];
  for (let i = 0; i < n; i++) {
    let aT: number;
    let bT: number;
    if (i % 2 === 0) {
      // 純一位小數 0.1..0.9(十分位整數 1..9)。
      aT = randInt(rng, 1, 9);
      bT = randInt(rng, 1, 9);
    } else {
      // 帶小數 a.x vs b.y(整數部 0..9,十分位 0..9)。
      aT = randInt(rng, 1, 9) * 10 + randInt(rng, 0, 9);
      bT = randInt(rng, 1, 9) * 10 + randInt(rng, 0, 9);
    }
    out.push({
      id: `cmp${i + 1}`,
      prompt: `比比看:${fmtTenths(aT)} (　) ${fmtTenths(bT)}　在()裡填 >、< 或 =。`,
      answer: recomputeDecimalAnswer({ op: 'compare', aT, bT })!,
      choices: COMPARE_CHOICES,
      verify: { op: 'compare', aT, bT },
    });
  }
  // 保證同卷含 '>' 與 '<'(避免全 '=' / 全同向);n≥2 時調整最後一題。
  if (n >= 2) {
    const hasGt = out.some((q) => q.answer === '>');
    const hasLt = out.some((q) => q.answer === '<');
    if (!hasGt || !hasLt) {
      const wantGt = !hasGt;
      const aT = wantGt ? 7 : 3;
      const bT = wantGt ? 3 : 7; // 7>3 → '>';3<7 → '<'
      out[n - 1] = {
        id: `cmp${n}`,
        prompt: `比比看:${fmtTenths(aT)} (　) ${fmtTenths(bT)}　在()裡填 >、< 或 =。`,
        answer: recomputeDecimalAnswer({ op: 'compare', aT, bT })!,
        choices: COMPARE_CHOICES,
        verify: { op: 'compare', aT, bT },
      };
    }
  }
  return out;
}

/**
 * ④ addsub 一位小數加減。十分位整數運算,涵蓋:
 *   - 純小數加(可進位):0.4+0.3、0.8+0.2=1.0。
 *   - 帶小數加:1.6+2.7。
 *   - 純小數減 / 整數減小數(退位):0.7-0.5、1-0.3。
 *   - 帶小數減:2.3-0.7。
 * 保證至少一題進位(和為整數)與一題退位(整數減小數)。
 */
function genAddSubQuestions(rng: () => number, n: number): DecimalQuestion[] {
  const out: DecimalQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const t = i % 4;
    if (t === 0) {
      // 純一位小數加,可進位(aT+bT ≤ 9 或恰 = 10/進位)。和 ≤ 1.x。
      const aT = randInt(rng, 1, 8);
      const bT = randInt(rng, 1, 10 - 1); // 和 ≤ 17(1.7)
      out.push(addQ(i, aT, bT));
    } else if (t === 1) {
      // 帶小數加:a.x + b.y,整數部 1..8、十分位 0..9。
      const aT = randInt(rng, 1, 8) * 10 + randInt(rng, 0, 9);
      const bT = randInt(rng, 1, 5) * 10 + randInt(rng, 0, 9);
      out.push(addQ(i, aT, bT));
    } else if (t === 2) {
      // 整數減小數(退位):整數 1..9 → ×10,減 0.1..0.9。
      const aT = randInt(rng, 1, 9) * 10;
      const bT = randInt(rng, 1, 9);
      out.push(subQ(i, aT, bT));
    } else {
      // 帶小數減:a.x - b.y(保證非負,aT ≥ bT)。
      let aT = randInt(rng, 2, 9) * 10 + randInt(rng, 0, 9);
      let bT = randInt(rng, 1, 8) * 10 + randInt(rng, 0, 9);
      if (bT > aT) [aT, bT] = [bT, aT];
      out.push(subQ(i, aT, bT));
    }
  }
  // 保證含一題進位(和為整十分位整數,如 1.0)與一題退位(整數減小數)。
  if (n >= 2) {
    const hasCarry = out.some((q) => q.verify.op === 'add' && (q.verify.aT + q.verify.bT) % 10 === 0);
    if (!hasCarry) {
      // 用 0.8+0.2=1.0 取代第一題。
      out[0] = addQ(0, 8, 2);
    }
    const hasBorrow = out.some(
      (q) => q.verify.op === 'sub' && q.verify.aT % 10 === 0 && q.verify.bT % 10 !== 0,
    );
    if (!hasBorrow) {
      // 用 1-0.3=0.7 取代最後一題。
      out[n - 1] = subQ(n - 1, 10, 3);
    }
  }
  return out;
}

function addQ(i: number, aT: number, bT: number): DecimalQuestion {
  return {
    id: `as${i + 1}`,
    prompt: `${fmtTenths(aT)} + ${fmtTenths(bT)} = ?`,
    answer: recomputeDecimalAnswer({ op: 'add', aT, bT })!,
    verify: { op: 'add', aT, bT },
  };
}

function subQ(i: number, aT: number, bT: number): DecimalQuestion {
  // 整數減小數時題幹用整數呈現(1 - 0.3),其餘用小數。
  const aStr = aT % 10 === 0 ? String(aT / 10) : fmtTenths(aT);
  return {
    id: `as${i + 1}`,
    prompt: `${aStr} − ${fmtTenths(bT)} = ?`,
    answer: recomputeDecimalAnswer({ op: 'sub', aT, bT })!,
    verify: { op: 'sub', aT, bT },
  };
}

/**
 * ⑤ apply 帶小數與應用。前半出毫米↔公分換算(KP6),後半出情境加減(果汁 / 麵粉 / 身高…)。
 */
function genApplyQuestions(rng: () => number, n: number): DecimalQuestion[] {
  const out: DecimalQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const t = i % 3;
    if (t === 0) {
      // 毫米 → 公分:n 毫米 = 0.n 公分。
      const mm = randInt(rng, 1, 9);
      out.push({
        id: `ap${i + 1}`,
        prompt: `1 毫米 = 0.1 公分。${mm} 毫米是幾公分?`,
        answer: recomputeDecimalAnswer({ op: 'mm-to-cm', mm })!,
        verify: { op: 'mm-to-cm', mm },
      });
    } else if (t === 1) {
      // 幾公分幾毫米 → 一位小數公分。
      const cm = randInt(rng, 1, 9);
      const mm = randInt(rng, 1, 9);
      out.push({
        id: `ap${i + 1}`,
        prompt: `一條繩子長 ${cm} 公分 ${mm} 毫米,用一位小數表示是幾公分?`,
        answer: recomputeDecimalAnswer({ op: 'cm-mm-to-dec', cm, mm })!,
        verify: { op: 'cm-mm-to-dec', cm, mm },
      });
    } else {
      // 情境加減(curated 模板,十分位整數運算)。
      const tpl = pick(rng, APPLY_TEMPLATES);
      if (tpl.mode === 'add') {
        const aT = randInt(rng, 1, 8) * 10 + randInt(rng, 0, 9);
        const bT = randInt(rng, 1, 4) * 10 + randInt(rng, 0, 9);
        const prompt = tpl.template.replace('{a}', fmtTenths(aT)).replace('{b}', fmtTenths(bT));
        out.push({
          id: `ap${i + 1}`,
          prompt,
          answer: recomputeDecimalAnswer({ op: 'apply-add', aT, bT, unit: tpl.unit })!,
          verify: { op: 'apply-add', aT, bT, unit: tpl.unit },
        });
      } else {
        // 減:保證非負(aT ≥ bT)。
        let aT = randInt(rng, 3, 9) * 10 + randInt(rng, 0, 9);
        let bT = randInt(rng, 1, 7) * 10 + randInt(rng, 0, 9);
        if (bT > aT) [aT, bT] = [bT, aT];
        const prompt = tpl.template.replace('{a}', fmtTenths(aT)).replace('{b}', fmtTenths(bT));
        out.push({
          id: `ap${i + 1}`,
          prompt,
          answer: recomputeDecimalAnswer({ op: 'apply-sub', aT, bT, unit: tpl.unit })!,
          verify: { op: 'apply-sub', aT, bT, unit: tpl.unit },
        });
      }
    }
  }
  return out;
}

// ───────────────────────────── 組卷 ─────────────────────────────

/**
 * 各題型題數(預設)。五大題(grounded 在課本第 5 章):
 *   convert 5×5=25(互換 / 位值)/ figure 4×5=20(看圖 / 數線)/ compare 5×4=20(比大小)/
 *   addsub 6×5=30(加減)/ apply 5×5=25(毫米換算 + 應用)。
 * 線上計分總分 = 25+20+20+30+25 = 120。
 */
const DEFAULT_COUNTS: Required<DecimalCounts> = {
  convert: 5,
  figure: 4,
  compare: 5,
  addsub: 6,
  apply: 5,
};

/** 固定出題順序(由淺入深):互換 → 看圖/數線 → 比大小 → 加減 → 應用。 */
const SECTION_ORDER: DecimalQuestionKind[] = ['convert', 'figure', 'compare', 'addsub', 'apply'];

const SECTION_META: Record<
  DecimalQuestionKind,
  { title: string; instructions: string; pointsEach: number }
> = {
  convert: {
    title: '分數與一位小數的互換',
    instructions:
      '把 1 個整體平分成 10 份,1 份 = 1/10 = 0.1。分母是 10 的分數,分子放到小數點後第一位(3/10 = 0.3);幾個 0.1 滿 10 個就進位成 1。每題 5 分。',
    pointsEach: 5,
  },
  figure: {
    title: '看圖與數線寫小數',
    instructions:
      '一張紙平分成 10 格,塗了幾格就是 0.幾 張;數線上每一大格平分成 10 小格,看箭頭指的位置寫出小數。每題 5 分。',
    pointsEach: 5,
  },
  compare: {
    title: '一位小數比大小',
    instructions:
      '先比整數部分(個位),個位一樣再比十分位(小數點後第一位)。在()裡填 >、< 或 =。每題 4 分。',
    pointsEach: 4,
  },
  addsub: {
    title: '一位小數的加減',
    instructions:
      '小數點對齊再算(十分位對十分位)。滿 10 個 0.1 要進位成 1;整數減小數時把 1 看成 10 個 0.1 來退位。每題 5 分。',
    pointsEach: 5,
  },
  apply: {
    title: '帶小數與生活應用',
    instructions:
      '1 毫米 = 0.1 公分。讀題列式後用小數加減算出答案,記得寫單位(公分 / 公升 / 公斤 / 公尺)。每題 5 分。',
    pointsEach: 5,
  },
};

const CN_SECTION_LABELS = ['一', '二', '三', '四', '五', '六'];

/**
 * 從受控閉集生一份小數考卷。同 seed → 同卷(可重現)。
 *
 * @param opts seed(必填)+ 可選題數 / id / title。
 * @returns 一份 `DecimalExam`(預設五大題:互換 / 看圖 / 比大小 / 加減 / 應用)。
 *   生成後建議過 `validateDecimalExam`。
 */
export function generateDecimalExam(opts: DecimalGenOpts): DecimalExam {
  const rng = makeRng(opts.seed);
  const counts: Required<DecimalCounts> = { ...DEFAULT_COUNTS, ...opts.counts };

  const sections: DecimalSection[] = SECTION_ORDER.map((kind, idx) => {
    const meta = SECTION_META[kind];
    const nn = counts[kind];
    let questions: DecimalQuestion[];
    if (kind === 'convert') {
      questions = genConvertQuestions(rng, nn);
    } else if (kind === 'figure') {
      questions = genFigureQuestions(rng, nn);
    } else if (kind === 'compare') {
      questions = genCompareQuestions(rng, nn);
    } else if (kind === 'addsub') {
      questions = genAddSubQuestions(rng, nn);
    } else {
      questions = genApplyQuestions(rng, nn);
    }
    return {
      kind,
      label: CN_SECTION_LABELS[idx] ?? String(idx + 1),
      title: meta.title,
      instructions: meta.instructions,
      pointsEach: meta.pointsEach,
      questions,
    };
  });

  return {
    id: opts.id ?? `decimal-${opts.seed}`,
    title: opts.title ?? `小數考卷 · 第 ${opts.seed} 回`,
    textbook: '康軒',
    scope: '三下 · 小數(分數↔一位小數 / 看圖數線 / 比大小 / 加減 / 毫米換算與應用)',
    sections,
  };
}

/** 一份考卷的線上計分總分(Σ pointsEach×題數)。 */
export function examTotalPoints(exam: DecimalExam): number {
  return exam.sections.reduce((sum, s) => sum + s.pointsEach * s.questions.length, 0);
}

// ───────────────────────────── 驗證器 ─────────────────────────────

/**
 * 驗證一份(產生)小數考卷是否合法。回**錯誤訊息清單**,空陣列 = 合法。
 *
 * 檢查(lockdown 不打地鼠):
 *   - id 唯一、prompt / answer 非空、每大題至少一題。
 *   - **每題 answer == recomputeDecimalAnswer(verify)**(反算一致,十分位整數運算)。
 *   - 不超綱硬規則:只一位小數;**無兩位小數 / 百分位 / 小數乘除 op**;數值鎖三下範圍。
 *   - **浮點防呆**:answer 格式必為「個位.十分位」一位小數(/^-?\d+\.\d 公分?$/);
 *     不得出現兩位以上小數(.05、.25)或浮點尾差(.30000…)。
 *   - 比大小題:正解在 ['>','<','='];減 / 應用題結果非負。
 */
export function validateDecimalExam(exam: DecimalExam): string[] {
  const errors: string[] = [];
  const seenIds = new Set<string>();

  if (exam.sections.length === 0) errors.push('考卷沒有任何大題');

  for (const section of exam.sections) {
    if (section.questions.length === 0) {
      errors.push(`${section.label}、${section.title}:沒有任何題目`);
    }
    for (const q of section.questions) {
      if (seenIds.has(q.id)) errors.push(`${q.id}: 重複的題 id`);
      seenIds.add(q.id);
      if (!q.prompt || q.prompt.trim() === '') {
        errors.push(`${q.id}: prompt 為空`);
        continue;
      }
      if (!q.answer || q.answer.trim() === '') {
        errors.push(`${q.id}: answer 為空`);
        continue;
      }
      const expected = recomputeDecimalAnswer(q.verify);
      if (expected === null) {
        errors.push(`${q.id}: verify 規格不合法(${q.verify.op})`);
        continue;
      }
      if (expected !== q.answer) {
        errors.push(`${q.id}: 反算「${expected}」≠ answer「${q.answer}」(${q.verify.op})`);
      }
      checkBounds(q, errors);
    }
  }

  return errors;
}

/** 不超綱硬規則:只一位小數、無浮點尾差、數值鎖三下、結果非負。 */
function checkBounds(q: DecimalQuestion, errors: string[]): void {
  const v = q.verify;
  // 分數題答 n/10、比大小題答 >/</= —— 不走「一位小數」格式檢查。
  if (v.op === 'dec-to-frac') {
    if (!/^\d+\/10$/.test(q.answer)) errors.push(`${q.id}: 分數答非 n/10 形(${q.answer})`);
  } else if (v.op === 'compare') {
    // 比大小正解形狀在下方 switch 檢查。
  } else {
    // 其餘小數題答必為「一位小數(可帶單位)」—— 不准兩位以上小數。
    // 抽出答案中的數字部分(去單位),必為「整數.單一位數」。
    const numMatch = q.answer.match(/-?\d+\.\d(?!\d)/);
    if (!numMatch) {
      errors.push(`${q.id}: 答案非一位小數形(${q.answer})`);
    }
    // 明確抓「兩位以上小數 / 浮點尾差」(超綱 / 防呆紅線)。
    if (/\.\d{2,}/.test(q.answer)) {
      errors.push(`${q.id}: 出現兩位以上小數(超綱 / 浮點誤差):${q.answer}`);
    }
  }

  // 各 op 範圍硬規則(對齊 recompute 的 null 條件,雙保險)。
  switch (v.op) {
    case 'frac-to-dec':
    case 'dec-to-frac':
    case 'shade':
      break; // 範圍已由 recompute 守(1..9)
    case 'count-tenths':
      if (v.count < 1 || v.count > 99) errors.push(`${q.id}: count 超範圍(${v.count})`);
      break;
    case 'numberline':
      if (v.maxInt < 1 || v.maxInt > 5) errors.push(`${q.id}: 數線 maxInt 超範圍(${v.maxInt})`);
      if (v.tenths % 10 === 0) errors.push(`${q.id}: 數線點落在整數端點(${v.tenths})`);
      break;
    case 'compare':
      if (q.answer !== '>' && q.answer !== '<' && q.answer !== '=') {
        errors.push(`${q.id}: 比大小正解非 >/</=(${q.answer})`);
      }
      break;
    case 'add':
      if (v.aT + v.bT > 200) errors.push(`${q.id}: 加法結果超範圍(>20)`);
      break;
    case 'sub':
      if (v.aT - v.bT < 0) errors.push(`${q.id}: 減法結果為負`);
      break;
    case 'apply-add':
      if (v.aT + v.bT > 300) errors.push(`${q.id}: 應用加法結果超範圍`);
      if (!v.unit) errors.push(`${q.id}: 應用題缺單位`);
      break;
    case 'apply-sub':
      if (v.aT - v.bT < 0) errors.push(`${q.id}: 應用減法結果為負`);
      if (!v.unit) errors.push(`${q.id}: 應用題缺單位`);
      break;
    default:
      break;
  }
}
